import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

const FONTS_URL =
  "https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600&display=swap";

function useFonts() {
  useEffect(() => {
    if (document.querySelector('link[href*="Instrument+Serif"]')) return;
    const l = document.createElement("link");
    l.rel = "stylesheet";
    l.href = FONTS_URL;
    document.head.appendChild(l);
  }, []);
}

const T = {
  g700: "#155c36", g600: "#1b7a47", g500: "#1e9254", g400: "#28b566",
  g200: "#a7d9bc", g100: "#d6f0e3", g50: "#eef8f3",
  n900: "#0f1117", n800: "#1c2130", n700: "#2d3549", n600: "#4a5268",
  n400: "#8a93a8", n300: "#b0b8cc", n200: "#d1d5e0", n150: "#e4e7ef",
  n100: "#eef0f5", n50: "#f7f8fa", white: "#ffffff",
  amber: "#d97706", amberBg: "#fffbeb", amberBorder: "#fde68a",
  blue: "#2563eb", blueBg: "#eff6ff", blueBorder: "#bfdbfe",
  serif: "'Instrument Serif', Georgia, serif",
  sans: "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif",
};

const Icon = ({ name, size = 20, color = "currentColor", strokeWidth = 1.6 }) => {
  const paths = {
    ArrowLeft:      <><path d="M19 12H5"/><path d="m12 19-7-7 7-7"/></>,
    ArrowRight:     <><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></>,
    CheckCircle:    <><circle cx="12" cy="12" r="10"/><path d="m9 12 2 2 4-4"/></>,
    Building:       <><rect width="16" height="20" x="4" y="2" rx="2"/><path d="M9 22v-4h6v4"/><path d="M8 6h.01M16 6h.01M12 6h.01M12 10h.01M8 10h.01M16 10h.01M8 14h.01M16 14h.01M12 14h.01"/></>,
    Briefcase:      <><rect width="20" height="14" x="2" y="7" rx="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></>,
    Users:          <><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></>,
    ShieldCheck:    <><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><path d="m9 12 2 2 4-4"/></>,
    Package:        <><path d="M16.5 9.4 7.55 4.24"/><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.29 7 12 12 20.71 7"/><line x1="12" y1="22" x2="12" y2="12"/></>,
    Heart:          <><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></>,
    TrendingUp:     <><polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/><polyline points="16 7 22 7 22 13"/></>,
    Handshake:      <><path d="M11 17a4 4 0 0 1-4-4V7l4-4 4 4v2"/><path d="m9 13 4 4 7-7"/><path d="M3 21l4-4"/><path d="M7 17l4 4"/></>,
    MapPin:         <><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0z"/><circle cx="12" cy="10" r="3"/></>,
    Mail:           <><rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></>,
    ChevronDown:    <><path d="m6 9 6 6 6-6"/></>,
    ChevronUp:      <><path d="m18 15-6-6-6 6"/></>,
    Zap:            <><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></>,
    Globe:          <><circle cx="12" cy="12" r="10"/><path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20"/><path d="M2 12h20"/></>,
    BarChart:       <><line x1="12" y1="20" x2="12" y2="10"/><line x1="18" y1="20" x2="18" y2="4"/><line x1="6" y1="20" x2="6" y2="16"/></>,
    Star:           <><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></>,
    Home:           <><path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></>,
    Leaf:           <><path d="M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.48 19 2c1 2 2 4.18 2 8 0 5.5-4.78 10-10 10z"/><path d="M2 21c0-3 1.85-5.36 5.08-6C9.5 14.52 12 13 13 12"/></>,
    ChevronRight:   <><path d="m9 18 6-6-6-6"/></>,
    Send:           <><path d="m22 2-7 20-4-9-9-4Z"/><path d="M22 2 11 13"/></>,
    Phone:          <><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13.5a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.34 3h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 10.09a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></>,
  };
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24"
      fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
      {paths[name]}
    </svg>
  );
};

/* ══ DATA ══ */

const PARTNER_TYPES = [
  {
    id: "property",
    icon: "Building",
    label: "Property managers and landlords",
    tagline: "Maintain your properties without the coordination headache",
    desc: "Whether you manage five units or five hundred, JiraniApp gives you a single platform to find, book, and pay verified professionals for maintenance, repairs, cleaning, and renovations across your entire portfolio. No more chasing contacts, no more paying cash at the door.",
    benefits: [
      "Single dashboard across all properties",
      "Documented job history per unit",
      "M-Pesa payments with full receipts",
      "Priority access to verified Pros",
      "Discounted rates on bulk bookings",
    ],
    stat: "300+",
    statLabel: "properties already managed through JiraniApp",
  },
  {
    id: "realestate",
    icon: "Home",
    label: "Real estate agencies",
    tagline: "Add home services as a value-add for every sale and rental",
    desc: "Offer your clients something no other agency does: seamless access to verified home service professionals from the moment they move in. Partner with JiraniApp to provide branded service credits, moving support, and ongoing home maintenance as part of your client experience.",
    benefits: [
      "Branded onboarding gift for new clients",
      "Moving and relocation service access",
      "Co-branded client communications",
      "Dedicated account manager",
      "Referral revenue on completed bookings",
    ],
    stat: "48 hrs",
    statLabel: "average time from move-in to first booking",
  },
  {
    id: "corporate",
    icon: "Briefcase",
    label: "Corporate companies",
    tagline: "Home maintenance as a staff benefit that actually gets used",
    desc: "Companies partnering with JiraniApp provide employees with a home services allowance or subsidised access to the platform. It reduces the personal admin burden on your team and positions you as an employer that cares about life outside the office. Simple to set up, easy to track.",
    benefits: [
      "Flexible monthly credit per employee",
      "HR dashboard for usage reporting",
      "Invoice billing to company account",
      "Custom service categories per plan",
      "Employee onboarding support",
    ],
    stat: "KES 4,200",
    statLabel: "avg. monthly benefit value per employee",
  },
  {
    id: "insurance",
    icon: "ShieldCheck",
    label: "Insurance companies",
    tagline: "Faster claims resolution through verified professional networks",
    desc: "For home insurance providers, JiraniApp offers a verified contractor network for claims-related repairs and emergency response. We provide documented job records, photo evidence, and structured invoicing that plugs directly into your claims workflow.",
    benefits: [
      "Pre-vetted contractors for claims jobs",
      "Structured invoicing for claims records",
      "Photo and job documentation included",
      "Emergency response prioritisation",
      "Custom SLA agreements available",
    ],
    stat: "24 hrs",
    statLabel: "average response time for emergency bookings",
  },
  {
    id: "hardware",
    icon: "Package",
    label: "Hardware and materials suppliers",
    tagline: "Reach Pros at the moment they are buying materials",
    desc: "JiraniPros on the platform spend significantly on materials for every job. A partnership with JiraniApp gives your brand direct visibility to active professionals at the point of purchase consideration, through co-branded promotions, loyalty programs, and in-app referrals.",
    benefits: [
      "In-app referrals to your stores or delivery",
      "Co-branded promotions for JiraniPros",
      "Loyalty program integration",
      "Direct brand visibility to active Pros",
      "Sponsored placement in relevant categories",
    ],
    stat: "KES 12,000",
    statLabel: "avg. materials spend per Pro per month",
  },
  {
    id: "ngo",
    icon: "Heart",
    label: "NGOs and government programs",
    tagline: "Deliver home improvement programs with verified professionals",
    desc: "For organisations running housing upgrade programs, skills development initiatives, or community maintenance schemes, JiraniApp provides access to our verified professional network, a managed booking layer, and reporting for program accountability. We have worked with programs across Nairobi, Mombasa, and Kisumu.",
    benefits: [
      "Managed booking on behalf of beneficiaries",
      "Program reporting and job documentation",
      "Access to certified professionals only",
      "Budget tracking per program or ward",
      "Flexible billing and invoicing structures",
    ],
    stat: "6 counties",
    statLabel: "active JiraniPro coverage across Kenya",
  },
];

const PROCESS_STEPS = [
  {
    n: "01",
    icon: "Mail",
    title: "Reach out",
    desc: "Send us a brief note at partners@jiraniapp.com or use the form below. Tell us who you are and what kind of partnership you have in mind. No pitch deck required at this stage.",
  },
  {
    n: "02",
    icon: "Phone",
    title: "Introductory call",
    desc: "A member of our partnerships team will set up a 30-minute call to understand your needs, walk you through what we offer, and assess whether there is a genuine fit. No pressure, no sales script.",
  },
  {
    n: "03",
    icon: "Handshake",
    title: "Proposal and agreement",
    desc: "If there is a fit, we put together a clear, written proposal covering the structure, pricing, and terms. We keep agreements simple and focused on what actually matters to both sides.",
  },
  {
    n: "04",
    icon: "Zap",
    title: "Go live",
    desc: "Once signed, our team handles the integration and onboarding. Most partners are live within two weeks. You get a dedicated contact for the first 90 days to make sure things run smoothly.",
  },
];

const FAQS = [
  {
    q: "Is there a minimum size or scale to become a partner?",
    a: "No. We work with everything from individual landlords managing two properties to large corporates with hundreds of employees. What matters is that there is a genuine use case, not the size of your organisation.",
  },
  {
    q: "How is billing handled for corporate and property partners?",
    a: "We offer monthly invoice billing for organisations, with usage reports broken down by property, department, or employee as needed. Payments can be made via M-Pesa, bank transfer, or cheque depending on your preference.",
  },
  {
    q: "Can we white-label or co-brand the experience for our clients?",
    a: "Yes, for real estate and corporate partners we offer co-branded onboarding flows and client communications. The extent of customisation depends on the partnership tier. We can walk you through options during the introductory call.",
  },
  {
    q: "How do we know the professionals sent to our properties are trustworthy?",
    a: "Every JiraniPro on the platform has been through identity verification and a background check before going live. For property and insurance partners who need higher assurance, we can restrict bookings to certified or higher-rated professionals only.",
  },
  {
    q: "What reporting do you provide?",
    a: "Partners get access to a reporting dashboard showing job history, spending by category or property, ratings received, and invoices. For program-based NGO partnerships, we can produce custom reports aligned to your accountability requirements.",
  },
  {
    q: "Do you have coverage outside Nairobi?",
    a: "Yes. We have active JiraniPros in Mombasa, Kisumu, Nakuru, Eldoret, and several other towns. Coverage varies by service category. If your operations are in a specific location, we can confirm coverage before you commit to anything.",
  },
];

/* ── Sub-components ── */

function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ borderBottom: `1px solid ${T.n100}` }}>
      <button onClick={() => setOpen(o => !o)} style={{
        width: "100%", textAlign: "left", background: "none", border: "none",
        padding: "22px 0", display: "flex", justifyContent: "space-between",
        alignItems: "center", cursor: "pointer", gap: 20, fontFamily: T.sans,
      }}>
        <span style={{ fontSize: 15, fontWeight: 400, color: T.n800, lineHeight: 1.5, letterSpacing: "-0.01em" }}>{q}</span>
        <span style={{
          width: 28, height: 28, borderRadius: "50%", flexShrink: 0,
          border: `1px solid ${open ? T.g500 : T.n200}`,
          background: open ? T.g500 : "transparent",
          color: open ? T.white : T.n400,
          display: "flex", alignItems: "center", justifyContent: "center",
          transition: "all .22s cubic-bezier(.4,0,.2,1)",
          transform: open ? "rotate(45deg)" : "none",
          fontSize: 18, fontWeight: 300, lineHeight: 1,
        }}>+</span>
      </button>
      <div style={{ overflow: "hidden", maxHeight: open ? 300 : 0, transition: "max-height .32s cubic-bezier(.4,0,.2,1)" }}>
        <p style={{ margin: "0 0 22px", fontSize: 15, color: T.n600, lineHeight: 1.75, paddingRight: 48, fontFamily: T.sans }}>{a}</p>
      </div>
    </div>
  );
}

function PartnerCard({ partner, isOpen, onToggle }) {
  return (
    <div style={{
      borderRadius: 16,
      border: `1.5px solid ${isOpen ? T.g200 : T.n150}`,
      background: isOpen ? T.g50 : T.white,
      overflow: "hidden",
      transition: "all .22s cubic-bezier(.4,0,.2,1)",
    }}>
      <button onClick={onToggle} style={{
        width: "100%", background: "none", border: "none", cursor: "pointer",
        padding: "24px", display: "flex", alignItems: "flex-start",
        justifyContent: "space-between", gap: 16, fontFamily: T.sans, textAlign: "left",
      }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 16, flex: 1, minWidth: 0 }}>
          <div style={{
            width: 44, height: 44, borderRadius: 12, flexShrink: 0,
            background: isOpen ? T.g100 : T.n50,
            border: `1.5px solid ${isOpen ? T.g200 : T.n150}`,
            display: "flex", alignItems: "center", justifyContent: "center",
            transition: "all .2s",
          }}>
            <Icon name={partner.icon} size={19} color={isOpen ? T.g600 : T.n400} strokeWidth={1.5} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: isOpen ? T.n900 : T.n800, letterSpacing: "-0.01em", marginBottom: 4 }}>
              {partner.label}
            </div>
            <div style={{ fontSize: 13, color: isOpen ? T.g700 : T.n400, lineHeight: 1.5, fontStyle: isOpen ? "normal" : "normal" }}>
              {partner.tagline}
            </div>
          </div>
        </div>
        <span style={{
          width: 28, height: 28, borderRadius: "50%", flexShrink: 0, marginTop: 8,
          border: `1px solid ${isOpen ? T.g500 : T.n200}`,
          background: isOpen ? T.g500 : "transparent",
          color: isOpen ? T.white : T.n400,
          display: "flex", alignItems: "center", justifyContent: "center",
          transition: "all .2s", transform: isOpen ? "rotate(45deg)" : "none",
          fontSize: 18, fontWeight: 300, lineHeight: 1,
        }}>+</span>
      </button>

      <div style={{
        overflow: "hidden",
        maxHeight: isOpen ? 600 : 0,
        transition: "max-height .38s cubic-bezier(.4,0,.2,1)",
      }}>
        <div style={{ padding: "0 24px 28px" }}>
          <div style={{ borderTop: `1px solid ${T.g100}`, paddingTop: 20, display: "flex", flexDirection: "column", gap: 20 }}>

            <p style={{ fontSize: 15, color: T.n600, lineHeight: 1.8, margin: 0 }}>{partner.desc}</p>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 10 }}>
              {partner.benefits.map((b, i) => (
                <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                  <span style={{ flexShrink: 0, marginTop: 2 }}>
                    <Icon name="CheckCircle" size={15} color={T.g500} strokeWidth={2} />
                  </span>
                  <span style={{ fontSize: 13, color: T.n700, lineHeight: 1.55 }}>{b}</span>
                </div>
              ))}
            </div>

            <div style={{
              display: "inline-flex", alignItems: "center", gap: 16,
              padding: "16px 20px", borderRadius: 12,
              background: T.white, border: `1px solid ${T.g100}`,
              alignSelf: "flex-start",
            }}>
              <div>
                <div style={{ fontFamily: T.serif, fontSize: 26, color: T.g600, fontWeight: 400, letterSpacing: "-0.03em", lineHeight: 1 }}>
                  {partner.stat}
                </div>
                <div style={{ fontSize: 12, color: T.n400, marginTop: 4, lineHeight: 1.4 }}>{partner.statLabel}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Contact form ── */
function ContactForm() {
  const [form, setForm] = useState({ name: "", org: "", email: "", type: "", message: "" });
  const [submitted, setSubmitted] = useState(false);
  const [focused, setFocused] = useState("");

  const inputStyle = (field) => ({
    width: "100%", padding: "13px 16px",
    border: `1px solid ${focused === field ? T.g500 : T.n200}`,
    borderRadius: 10, fontSize: 14, outline: "none",
    background: T.white, color: T.n900, fontFamily: T.sans,
    boxShadow: focused === field ? `0 0 0 4px ${T.g100}` : "none",
    transition: "all .18s",
  });

  const labelStyle = {
    display: "block", fontSize: 13, fontWeight: 600,
    color: T.n700, marginBottom: 6, letterSpacing: "-0.01em",
  };

  if (submitted) {
    return (
      <div style={{
        padding: "48px 32px", textAlign: "center",
        border: `1.5px solid ${T.g200}`, borderRadius: 20,
        background: T.g50,
      }}>
        <div style={{ width: 52, height: 52, borderRadius: "50%", background: T.g500, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px" }}>
          <Icon name="CheckCircle" size={24} color={T.white} strokeWidth={2} />
        </div>
        <h3 style={{ fontFamily: T.serif, fontSize: 24, fontWeight: 400, color: T.n900, margin: "0 0 10px", letterSpacing: "-0.02em" }}>
          Message received
        </h3>
        <p style={{ fontSize: 15, color: T.n600, lineHeight: 1.75, margin: "0 auto", maxWidth: 380 }}>
          Someone from our partnerships team will be in touch within two business days. We look forward to the conversation.
        </p>
      </div>
    );
  }

  return (
    <div style={{ border: `1px solid ${T.n150}`, borderRadius: 20, padding: "clamp(28px,5vw,40px)", background: T.white }}>
      <div style={{ marginBottom: 28 }}>
        <p style={{ fontSize: 11, fontWeight: 600, color: T.g500, letterSpacing: "0.1em", textTransform: "uppercase", margin: "0 0 8px" }}>Get in touch</p>
        <h3 style={{ fontFamily: T.serif, fontSize: "clamp(22px,3.5vw,30px)", fontWeight: 400, color: T.n900, margin: "0 0 8px", letterSpacing: "-0.03em", lineHeight: 1.15 }}>
          Tell us about your <em style={{ fontStyle: "italic", color: T.g600 }}>organisation</em>
        </h3>
        <p style={{ fontSize: 14, color: T.n400, margin: 0, lineHeight: 1.65 }}>
          Fill in the form and we will come back to you within two business days. Or email us directly at{" "}
          <a href="mailto:partners@jiraniapp.com" style={{ color: T.g600, textDecoration: "none", borderBottom: `1px solid ${T.g200}` }}>
            partners@jiraniapp.com
          </a>
        </p>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 14 }}>
          <div>
            <label style={labelStyle}>Your name</label>
            <input
              value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              onFocus={() => setFocused("name")} onBlur={() => setFocused("")}
              placeholder="Full name" style={inputStyle("name")} />
          </div>
          <div>
            <label style={labelStyle}>Organisation</label>
            <input
              value={form.org} onChange={e => setForm(f => ({ ...f, org: e.target.value }))}
              onFocus={() => setFocused("org")} onBlur={() => setFocused("")}
              placeholder="Company or organisation name" style={inputStyle("org")} />
          </div>
        </div>

        <div>
          <label style={labelStyle}>Email address</label>
          <input
            type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
            onFocus={() => setFocused("email")} onBlur={() => setFocused("")}
            placeholder="your@organisation.com" style={inputStyle("email")} />
        </div>

        <div>
          <label style={labelStyle}>Partnership type</label>
          <div style={{ position: "relative" }}>
            <select
              value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))}
              onFocus={() => setFocused("type")} onBlur={() => setFocused("")}
              style={{ ...inputStyle("type"), appearance: "none", WebkitAppearance: "none", cursor: "pointer", paddingRight: 40 }}>
              <option value="">Select the type that fits best</option>
              <option value="property">Property manager or landlord</option>
              <option value="realestate">Real estate agency</option>
              <option value="corporate">Corporate company</option>
              <option value="insurance">Insurance company</option>
              <option value="hardware">Hardware or materials supplier</option>
              <option value="ngo">NGO or government program</option>
              <option value="other">Something else</option>
            </select>
            <span style={{ position: "absolute", right: 14, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }}>
              <Icon name="ChevronDown" size={16} color={T.n400} strokeWidth={2} />
            </span>
          </div>
        </div>

        <div>
          <label style={labelStyle}>Tell us a bit more</label>
          <textarea
            value={form.message} onChange={e => setForm(f => ({ ...f, message: e.target.value }))}
            onFocus={() => setFocused("msg")} onBlur={() => setFocused("")}
            placeholder="What are you looking to achieve through a partnership with JiraniApp? Even a few lines helps us prepare for the conversation."
            rows={4}
            style={{ ...inputStyle("msg"), resize: "vertical", lineHeight: 1.65 }} />
        </div>

        <button
          onClick={() => {
            if (!form.name || !form.email) return;
            setSubmitted(true);
          }}
          style={{
            padding: "14px 32px", background: T.g500, color: T.white,
            border: "none", borderRadius: 11, fontSize: 15, fontWeight: 600,
            fontFamily: T.sans, cursor: "pointer", letterSpacing: "0.01em",
            display: "inline-flex", alignItems: "center", gap: 8,
            alignSelf: "flex-start", transition: "background .18s",
          }}
          onMouseEnter={e => e.currentTarget.style.background = T.g600}
          onMouseLeave={e => e.currentTarget.style.background = T.g500}>
          Send your message
          <Icon name="Send" size={15} color={T.white} strokeWidth={2} />
        </button>

        <p style={{ fontSize: 12, color: T.n300, margin: 0 }}>
          We respond to every enquiry within two business days. No sales calls without your consent.
        </p>
      </div>
    </div>
  );
}

/* ══ MAIN PAGE ══ */
export default function PartnerWithUsPage() {
  useFonts();
  const navigate = useNavigate();
  const [openPartner, setOpenPartner] = useState(null);
  const formRef = useState(null);

  const scrollToForm = () => {
    document.getElementById("partner-form")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div style={{ fontFamily: T.sans, background: T.white, minHeight: "100vh", color: T.n900, paddingBottom: 80 }}>
      <style>{`
        * { box-sizing: border-box; }
        ::-webkit-scrollbar { display: none; }
      `}</style>

      {/* ── Nav ── */}
      <div style={{ padding: "16px 24px", borderBottom: `1px solid ${T.n100}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <button onClick={() => navigate(-1)} style={{
          display: "inline-flex", alignItems: "center", gap: 6,
          background: "none", border: "none", cursor: "pointer",
          color: T.n400, fontSize: 13, fontFamily: T.sans, padding: 0,
        }}
          onMouseEnter={e => e.currentTarget.style.color = T.n800}
          onMouseLeave={e => e.currentTarget.style.color = T.n400}>
          <Icon name="ArrowLeft" size={14} strokeWidth={2} />
          Back
        </button>
        <button onClick={scrollToForm} style={{
          display: "inline-flex", alignItems: "center", gap: 6,
          background: T.g500, border: "none", cursor: "pointer",
          color: T.white, fontSize: 13, fontFamily: T.sans,
          padding: "8px 18px", borderRadius: 8, fontWeight: 600,
        }}
          onMouseEnter={e => e.currentTarget.style.background = T.g600}
          onMouseLeave={e => e.currentTarget.style.background = T.g500}>
          Get in touch
          <Icon name="ArrowRight" size={13} color={T.white} strokeWidth={2} />
        </button>
      </div>

      {/* ══ HERO ══ */}
      <div style={{ padding: "clamp(56px,8vw,100px) 20px clamp(48px,6vw,80px)", borderBottom: `1px solid ${T.n100}` }}>
        <div style={{ maxWidth: 720, margin: "0 auto" }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, color: T.g600, fontSize: 12, fontWeight: 600, letterSpacing: "0.09em", textTransform: "uppercase", marginBottom: 24 }}>
            <Icon name="Handshake" size={14} color={T.g500} strokeWidth={2.2} />
            Partnerships
          </div>
          <h1 style={{ fontFamily: T.serif, fontSize: "clamp(40px,7.5vw,70px)", fontWeight: 400, margin: "0 0 22px", lineHeight: 1.05, color: T.n900, letterSpacing: "-0.03em", maxWidth: 640 }}>
            Build something better<br />
            <em style={{ fontStyle: "italic", color: T.g600 }}>together</em>
          </h1>
          <p style={{ fontSize: "clamp(15px,2vw,17px)", color: T.n600, lineHeight: 1.8, margin: "0 0 36px", maxWidth: 520 }}>
            JiraniApp works with property managers, real estate agencies, corporates, insurers, hardware suppliers, and government programs to bring reliable home services to more Kenyans. If you have a use case, we want to hear it.
          </p>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
            <button onClick={scrollToForm} style={{
              display: "inline-flex", alignItems: "center", gap: 8,
              background: T.g500, color: T.white, border: "none",
              padding: "15px 30px", borderRadius: 11, fontSize: 15, fontWeight: 600,
              cursor: "pointer", fontFamily: T.sans, letterSpacing: "0.01em",
            }}
              onMouseEnter={e => e.currentTarget.style.background = T.g600}
              onMouseLeave={e => e.currentTarget.style.background = T.g500}>
              Start a conversation
              <Icon name="ArrowRight" size={16} color={T.white} strokeWidth={2} />
            </button>
            <span style={{ fontSize: 13, color: T.n400 }}>Response within two business days</span>
          </div>
        </div>
      </div>

      {/* ══ TRUST STRIP ══ */}
      <div style={{ borderBottom: `1px solid ${T.n100}`, padding: "0 20px" }}>
        <div style={{ maxWidth: 720, margin: "0 auto", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))" }}>
          {[
            { icon: "Globe",    label: "Active in Nairobi, Mombasa, Kisumu and more" },
            { icon: "Users",    label: "700+ verified professionals on the platform" },
            { icon: "BarChart", label: "Structured reporting and invoicing for every partner" },
          ].map(({ icon, label }, i) => (
            <div key={i} style={{ padding: "22px 20px", display: "flex", alignItems: "center", gap: 12, borderRight: i < 2 ? `1px solid ${T.n100}` : "none" }}>
              <Icon name={icon} size={18} color={T.g500} strokeWidth={1.5} />
              <span style={{ fontSize: 13, color: T.n600, lineHeight: 1.4 }}>{label}</span>
            </div>
          ))}
        </div>
      </div>

      <div style={{ maxWidth: 720, margin: "0 auto", padding: "0 20px" }}>

        {/* ══ PARTNER TYPES ══ */}
        <div style={{ paddingTop: "clamp(48px,7vw,72px)", marginBottom: "clamp(48px,7vw,72px)" }}>
          <p style={{ fontSize: 11, fontWeight: 600, color: T.g500, letterSpacing: "0.1em", textTransform: "uppercase", margin: "0 0 12px" }}>Who we work with</p>
          <h2 style={{ fontFamily: T.serif, fontSize: "clamp(26px,4.5vw,36px)", fontWeight: 400, color: T.n900, margin: "0 0 8px", letterSpacing: "-0.03em", lineHeight: 1.15 }}>
            Find your <em style={{ fontStyle: "italic", color: T.g600 }}>partnership model</em>
          </h2>
          <p style={{ fontSize: 15, color: T.n400, margin: "0 0 28px", lineHeight: 1.65 }}>
            We have built partnership structures around the six types of organisations we work with most. Select any category to see how it works in detail.
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {PARTNER_TYPES.map(p => (
              <PartnerCard
                key={p.id}
                partner={p}
                isOpen={openPartner === p.id}
                onToggle={() => setOpenPartner(openPartner === p.id ? null : p.id)}
              />
            ))}
          </div>
        </div>

        {/* ══ HOW IT WORKS ══ */}
        <div style={{ marginBottom: "clamp(48px,7vw,72px)" }}>
          <p style={{ fontSize: 11, fontWeight: 600, color: T.g500, letterSpacing: "0.1em", textTransform: "uppercase", margin: "0 0 12px" }}>The process</p>
          <h2 style={{ fontFamily: T.serif, fontSize: "clamp(26px,4.5vw,36px)", fontWeight: 400, color: T.n900, margin: "0 0 8px", letterSpacing: "-0.03em", lineHeight: 1.15 }}>
            From first message to <em style={{ fontStyle: "italic", color: T.g600 }}>live in four steps</em>
          </h2>
          <p style={{ fontSize: 15, color: T.n400, margin: "0 0 32px", lineHeight: 1.65 }}>
            We keep the partnership process straightforward. No lengthy procurement cycles, no committee presentations unless that is your process.
          </p>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 14 }}>
            {PROCESS_STEPS.map((step, i) => (
              <div key={i} style={{
                padding: "24px", borderRadius: 16,
                border: `1px solid ${T.n150}`, background: T.n50,
                display: "flex", flexDirection: "column", gap: 14,
              }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <div style={{
                    width: 42, height: 42, borderRadius: 12,
                    background: T.white, border: `1.5px solid ${T.g100}`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    <Icon name={step.icon} size={18} color={T.g500} strokeWidth={1.6} />
                  </div>
                  <span style={{
                    fontSize: 11, fontWeight: 700, color: T.n300,
                    letterSpacing: "0.1em",
                  }}>{step.n}</span>
                </div>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 600, color: T.n900, marginBottom: 8, letterSpacing: "-0.01em" }}>{step.title}</div>
                  <p style={{ fontSize: 13, color: T.n600, lineHeight: 1.75, margin: 0 }}>{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ══ WHY JIRANIAPP ══ */}
        <div style={{
          marginBottom: "clamp(48px,7vw,72px)",
          padding: "clamp(28px,5vw,44px)",
          borderRadius: 20, background: T.g50, border: `1px solid ${T.g100}`,
        }}>
          <p style={{ fontSize: 11, fontWeight: 600, color: T.g500, letterSpacing: "0.1em", textTransform: "uppercase", margin: "0 0 12px" }}>Why JiraniApp</p>
          <h2 style={{ fontFamily: T.serif, fontSize: "clamp(24px,4vw,32px)", fontWeight: 400, color: T.n900, margin: "0 0 24px", letterSpacing: "-0.03em", lineHeight: 1.2 }}>
            What you get when you <em style={{ fontStyle: "italic", color: T.g600 }}>partner with us</em>
          </h2>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 20 }}>
            {[
              { icon: "ShieldCheck", title: "Verified professionals only", desc: "Every JiraniPro is ID-verified and background-checked before going live. You are not pulling from an unvetted pool." },
              { icon: "BarChart",    title: "Full reporting and documentation", desc: "Every job is recorded with timestamps, photos, and structured invoices. Your finance team will thank you." },
              { icon: "MapPin",      title: "Coverage across Kenya", desc: "We are not Nairobi-only. Active coverage in Mombasa, Kisumu, Nakuru, Eldoret, and growing. Useful if your operations span multiple cities." },
              { icon: "Phone",       title: "A team behind the partnership", desc: "You get a dedicated partner contact, not a support ticket queue. We are invested in making the partnership work, not just closing it." },
              { icon: "Star",        title: "Quality that is measurable", desc: "Every completed job comes with a client rating. You can set minimum rating requirements for jobs on your account, and we monitor compliance." },
              { icon: "Zap",         title: "Fast to set up", desc: "Most partners are live within two weeks of signing. We handle the setup, the onboarding, and the first 90 days of support." },
            ].map((item, i) => (
              <div key={i} style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
                <div style={{ width: 38, height: 38, borderRadius: 10, background: T.white, border: `1px solid ${T.g100}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <Icon name={item.icon} size={16} color={T.g500} strokeWidth={1.7} />
                </div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: T.g800 || T.n800, marginBottom: 5, letterSpacing: "-0.01em" }}>{item.title}</div>
                  <p style={{ fontSize: 13, color: T.g700, lineHeight: 1.7, margin: 0 }}>{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ══ FAQ ══ */}
        <div style={{ marginBottom: "clamp(48px,7vw,72px)" }}>
          <p style={{ fontSize: 11, fontWeight: 600, color: T.g500, letterSpacing: "0.1em", textTransform: "uppercase", margin: "0 0 12px" }}>Common questions</p>
          <h2 style={{ fontFamily: T.serif, fontSize: "clamp(26px,4.5vw,36px)", fontWeight: 400, color: T.n900, margin: "0 0 4px", letterSpacing: "-0.03em", lineHeight: 1.15 }}>
            Before you <em style={{ fontStyle: "italic", color: T.g600 }}>reach out</em>
          </h2>
          <p style={{ fontSize: 15, color: T.n400, margin: "0 0 8px", lineHeight: 1.65 }}>
            The questions we get most often from prospective partners.
          </p>
          <div>
            {FAQS.map((item, i) => <FaqItem key={i} {...item} />)}
          </div>
        </div>

        {/* ══ FORM ══ */}
        <div id="partner-form" style={{ marginBottom: 20, scrollMarginTop: 40 }}>
          <ContactForm />
        </div>

      </div>
    </div>
  );
}